import { Close as CloseIcon } from 'components/icons';
import { format } from 'date-fns';
import React, { ReactNode } from 'react';

import { ChipType } from 'components/@extended/StatusChip';
import { RowChip } from 'components/GridItemList';
import PartyName, { PartyNameVariant } from 'components/party/PartyName';
import { EmployeeNameLink, RecordLink } from 'components/record-chip/RecordLink';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { MatterStateType, MinDtoWithSuffix } from 'types/matters';
import { MinDto } from 'types/other';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { renderMatterIndicators } from 'utils/matterOptionRenderer';
import { isNewDesignCopy } from 'utils/newDesignCopy';
import { PartyNameParty } from 'utils/partyNameUtil';

/**
 * An entity link: body text at rest, the link colour and an underline on hover. `entity-link` hands the
 * link's colour to the detail-row and table-cell rules around it.
 */
export const ENTITY_LINK_CLASS = 'entity-link text-fg hover:text-link w-fit no-underline hover:underline';

type getLinksProps = MinDto & {
    /** Rendered before the link — where the case indicators go, since an icon leads what it describes. */
    prefix?: ReactNode;
    suffix?: ReactNode;
    // Present for employee/person items; lets the non-contact branch flip to "Last First".
    firstName?: string;
    lastName?: string;
};

/** One record of a {@link getLinks} list. The new design spaces the records' chips apart, with no separator. */
function LinksItem({ separator, children }: { separator: ReactNode; children: ReactNode }) {
    return useIsNewDesign() ? (
        <span className="me-1 inline-flex max-w-full items-center">{children}</span>
    ) : (
        <span style={{ display: 'contents' }}>
            {children}
            {separator}
        </span>
    );
}

type Options<T extends 'inline-comma-separated' | 'vertical' = 'inline-comma-separated'> = {
    separator?: string | null;
    /** Colours the link at rest as well as on hover. */
    linkColor?: 'text.link';
    renderMethod?: T;
    target?: string;
    onClick?: () => void;
    partyVariant?: PartyNameVariant;
    /** Forwarded to `<PartyName>` when `domain === 'contact'`. */
    showPartyNumber?: boolean;
    /** Forwarded to `<PartyName>` when `domain === 'contact'`. */
    useLinkWithOrigin?: boolean;
};

// Overload signatures
export function getLinks(
    domain: string,
    listItems: getLinksProps[],
    options?: Options<'inline-comma-separated'>
): React.ReactNode[];
export function getLinks(domain: string, listItems: getLinksProps[], options: Options<'vertical'>): React.ReactNode;
export function getLinks(domain: string, listItems: getLinksProps[]): React.ReactNode[];

// Implementation
export function getLinks(
    domain: string,
    listItems: getLinksProps[],
    options?: Options<'inline-comma-separated' | 'vertical'>
): React.ReactNode[] | React.ReactNode {
    const {
        separator = `,\u00A0`,
        linkColor,
        renderMethod = 'inline-comma-separated',
        target,
        onClick,
        partyVariant = 'readonly-other',
        showPartyNumber = false,
        useLinkWithOrigin = false,
    } = options || {};
    const links: React.ReactNode[] = [];
    const isContact = domain === 'contact';
    listItems
        .toSorted((a, b) => {
            if (a.name && b.name) {
                return a.name.localeCompare(b.name);
            }
            return -1;
        })
        .forEach((item, index) => {
            const optionalComma = index < listItems.length - 1 ? separator : null;
            links.push(
                <LinksItem
                    key={`${domain}-${item.id}`}
                    separator={renderMethod === 'inline-comma-separated' && optionalComma}
                >
                    {item.prefix}
                    {isContact ? (
                        <PartyName
                            party={item as PartyNameParty}
                            isLink
                            useLinkWithOrigin={useLinkWithOrigin}
                            linkTarget={target}
                            variant={partyVariant}
                            showPartyNumber={showPartyNumber}
                        />
                    ) : (
                        <EmployeeNameLink
                            className={cn(ENTITY_LINK_CLASS, linkColor && 'text-link')}
                            to={`/${domain}/${item.id}`}
                            entity={item}
                            onClick={onClick}
                            target={target}
                            rel={target === '_blank' ? 'noopener noreferrer' : undefined}
                        />
                    )}
                    {item.suffix ? <span className="ml-[.5ch]">{item.suffix}</span> : null}
                </LinksItem>
            );
        });
    if (links.length == 0) {
        links.push('-');
    }
    if (renderMethod === 'vertical') {
        return <div className="flex flex-col">{links}</div>;
    }
    return links;
}

/**
 * The values of a multi-select as the chips its field draws in a record section, each the link `getLinks` gives
 * it, which the new design draws as the record's chip. In the order given, which is the field's; `showEmail` is for a
 * contact field whose chips carry the e-mail.
 */
export function getLinkChips(domain: string, listItems: getLinksProps[], showEmail = false): RowChip[] {
    return listItems.map((item) => ({
        key: item.id,
        record: true,
        label:
            domain === 'contact' ? (
                <PartyName party={item as PartyNameParty} isLink variant="readonly-other" showEmail={showEmail} />
            ) : (
                <EmployeeNameLink className={ENTITY_LINK_CLASS} to={`/${domain}/${item.id}`} entity={item} />
            ),
    }));
}

/** Cases as the chips their multi-select draws in a record section: the case's indicators, then its link. */
export const getCaseChips = (
    matters: (Parameters<typeof renderMatterIndicators>[0] & { id: number; fullName: string })[]
): RowChip[] =>
    matters.map((matter) => ({
        key: matter.id,
        icon: renderMatterIndicators(matter),
        record: true,
        label: <RecordLink className={ENTITY_LINK_CLASS} to={`/matter/${matter.id}`} label={matter.fullName} />,
    }));

export const getDeletableLinks = (
    domain: string,
    listItems: MinDtoWithSuffix[],
    deleteButtonHandler: (id: number) => void,
    /** Not read: colours come from the tokens. */
    _theme?: unknown,
    forceAddComma = false
): React.ReactNode[] => {
    const links: React.ReactNode[] = [];
    listItems
        .toSorted((a: MinDtoWithSuffix, b: MinDtoWithSuffix) => a.name.localeCompare(b.name))
        .forEach((item, index) => {
            const optionalComma = index < listItems.length - 1 || forceAddComma ? `,\u00A0` : null;
            links.push(
                <div key={item.id} className="text-link flex items-center gap-0.5">
                    {item.prefix}
                    <RecordLink className={ENTITY_LINK_CLASS} to={`/${domain}/${item.id}`} label={item.name} />
                    {item.suffix ? <span className="text-fg ml-1">{item.suffix}</span> : null}
                    {getDeleteButton(item.id, item.name, deleteButtonHandler)}
                    {optionalComma}
                </div>
            );
        });
    return links;
};

const getDeleteButton = (id: number, name: string, deleteButtonHandler: (id: number) => void): React.ReactNode => {
    return (
        <button
            type="button"
            aria-label={t('remove-name', { name })}
            onClick={() => deleteButtonHandler(id)}
            className="bg-divider hover:bg-grey-400 inline-flex size-3 shrink-0 cursor-pointer items-center justify-center rounded-full p-0 text-[10px] text-white"
        >
            <CloseIcon fontSize="inherit" />
        </button>
    );
};

export const getStatusChipType = (value?: string) => {
    let statusChipType: ChipType;
    switch (value) {
        case MatterStateType.ARCHIVED:
            statusChipType = 'outlined';
            break;
        case MatterStateType.COMPLETED:
            statusChipType = 'neutral';
            break;
        case MatterStateType.IN_PROGRESS:
            statusChipType = 'success';
            break;
        case MatterStateType.OFFER_LOST:
            statusChipType = 'inactive';
            break;
        case MatterStateType.OFFER_PENDING:
            statusChipType = 'warning';
            break;
        case MatterStateType.OFFER_WON:
            statusChipType = 'finalized';
            break;
    }
    return statusChipType;
};

// A matter's fullName is "<caseNumber> <name>" (BE @Formula concat), so when the case number is already shown
// separately, strip it from fullName to avoid displaying the number twice. Falls back to the full value when the
// prefix is absent (e.g. legacy data or a name that does not start with the number).
export const matterNameWithoutNumber = (caseNumber: string, fullName: string): string => {
    const trimmed = (fullName ?? '').trim();
    if (caseNumber && trimmed.startsWith(caseNumber)) {
        return trimmed.slice(caseNumber.length).trim() || trimmed;
    }
    return trimmed;
};

// Formats a delegation substitute period for display next to a substitute's name: given the company's date format,
// the new design writes both days in it, in one message; classic writes "DD Mon – DD Mon YYYY".
export const formatSubstitutePeriod = (startDate: string, endDate: string, dateFormat?: string): string =>
    isNewDesignCopy() && dateFormat
        ? t('date-period', {
              start: format(new Date(startDate), dateFormat),
              end: format(new Date(endDate), dateFormat),
          })
        : `${format(new Date(startDate), 'dd MMM')} – ${format(new Date(endDate), 'dd MMM yyyy')}`;

// Same period, prefixed with "· " for use as an inline suffix after the substitute's name.
export const formatSubstitutePeriodSuffix = (startDate: string, endDate: string, dateFormat?: string): string =>
    `· ${formatSubstitutePeriod(startDate, endDate, dateFormat)}`;
